import { motion } from "framer-motion";
import { 
  BrainCircuit, 
  PaintBucket, 
  PenTool, 
  Apple, 
  Smartphone, 
  Palette, 
  Globe, 
  Tv, 
  Users 
} from "lucide-react";
import { useEffect, useState } from "react";

interface PillItem {
  id: number;
  text: string;
  icon: React.ReactNode;
}

export const RunningPills = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsVisible(true);
    }, 400);

    return () => clearTimeout(timeout);
  }, []);

  const pillItems: PillItem[] = [
    { id: 1, text: "AI/ML", icon: <BrainCircuit size={16} className="text-coral" /> },
    { id: 2, text: "Branding", icon: <PaintBucket size={16} className="text-coral" /> },
    { id: 3, text: "Design", icon: <PenTool size={16} className="text-coral" /> },
    { id: 4, text: "iOS", icon: <Apple size={16} className="text-coral" /> },
    { id: 5, text: "Android", icon: <Smartphone size={16} className="text-coral" /> },
    { id: 6, text: "UI/UX", icon: <Palette size={16} className="text-coral" /> },
    { id: 7, text: "Web", icon: <Globe size={16} className="text-coral" /> },
    { id: 8, text: "tvOS", icon: <Tv size={16} className="text-coral" /> },
    { id: 9, text: "Consulting", icon: <Users size={16} className="text-coral" /> },
  ];

  // Triple the items to ensure smooth looping
  const firstRowItems = [...pillItems, ...pillItems, ...pillItems];
  const secondRowItems = [...pillItems, ...pillItems, ...pillItems];

  return (
    <section className="relative pb-6 md:pb-8 bg-background overflow-hidden">
      <div className="max-w-[97%] mx-auto">
        {/* First row - Left to Right */}
        <div className="relative mb-5 overflow-hidden">
          <motion.div
            className="flex whitespace-nowrap"
            initial={{ x: "-100%" }}
            animate={{ x: isVisible ? "-50%" : "-100%" }}
            transition={{
              repeat: Infinity,
              repeatType: "loop",
              duration: 8,
              ease: "linear",
            }}
          >
            {firstRowItems.map((item, index) => (
              <motion.div
                key={`${item.id}-${index}`}
                className="inline-flex items-center justify-center px-10 py-1.5 mx-4 min-w-[170px] bg-[#002a45]/40 text-white rounded-full shadow-md border border-[#003456]/50 transition-all duration-300"
              >
                <div className="bg-[#00223a] p-1 rounded-full mr-2.5 flex items-center justify-center">
                  {item.icon}
                </div>
                <span className="font-medium tracking-wide text-xs">{item.text}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Second row - Right to Left */}
        <div className="relative overflow-hidden">
          <motion.div
            className="flex whitespace-nowrap"
            initial={{ x: "0%" }}
            animate={{ x: isVisible ? "-50%" : "0%" }}
            transition={{
              repeat: Infinity,
              repeatType: "loop",
              duration: 10,
              ease: "linear",
            }}
          >
            {secondRowItems.reverse().map((item, index) => (
              <motion.div
                key={`${item.id}-${index}`}
                className="inline-flex items-center justify-center px-10 py-1.5 mx-4 min-w-[170px] bg-[#002a45]/40 text-white rounded-full shadow-md border border-[#003456]/50 transition-all duration-300"
              >
                <div className="bg-[#00223a] p-1 rounded-full mr-2.5 flex items-center justify-center">
                  {item.icon}
                </div>
                <span className="font-medium tracking-wide text-xs">{item.text}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
      
      {/* Bottom gradient for smooth visual transition */}
      <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-background to-transparent"></div>
    </section>
  );
}; 